import { DragOutlined, SettingOutlined } from '@ant-design/icons';
import {
  Col, Modal, Row, Select, Spin, Switch, Typography,
} from 'antd';
import GraficaCard, { MODULOS, TIPOS_GRAFICA } from 'components/GraficaConfigurableCard';
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import GridLayout, { WidthProvider } from 'react-grid-layout';
import API from 'utils/api';
import { onError } from 'utils/handlers';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const { Title } = Typography;
const { Option } = Select;

const GridLayoutConAncho = WidthProvider(GridLayout);
const COLUMNAS = 3;
const ALTURA_FILA = 320;

const combinarLayoutGuardado = (guardado, graficas) => {
  const porId = new Map((guardado || []).map((item) => [String(item.grafica), item]));
  let siguienteY = 0;
  (guardado || []).forEach((item) => { siguienteY = Math.max(siguienteY, item.y + item.h); });
  let siguienteX = 0;

  return graficas.map((g) => {
    const key = String(g.id);
    const existente = porId.get(key);
    if (existente) {
      return {
        i: key, x: existente.x, y: existente.y, w: existente.w, h: existente.h,
      };
    }
    const item = {
      i: key, x: siguienteX, y: siguienteY, w: 1, h: 1,
    };
    siguienteX += 1;
    if (siguienteX >= COLUMNAS) {
      siguienteX = 0;
      siguienteY += 1;
    }
    return item;
  });
};

const Dashboard = () => {
  const [loading, setLoading] = useState(false);
  const [graficas, setGraficas] = useState([]);
  const [modulosOcultos, setModulosOcultos] = useState([]);
  const [layoutsPorModulo, setLayoutsPorModulo] = useState({});
  const [showPersonalizar, setShowPersonalizar] = useState(false);

  const fetchDatos = useCallback(async () => {
    try {
      setLoading(true);
      const [resGraficas, resModulos, ...resLayouts] = await Promise.all([
        API.get('configuraciones/configuraciones-graficas/'),
        API.get('configuraciones/modulos-ocultos/'),
        ...MODULOS.map((m) => API.get('configuraciones/layout-dashboard/', { params: { modulo: m.value } })),
      ]);
      setGraficas(resGraficas?.data || []);
      setModulosOcultos(resModulos?.data?.modulos_ocultos || []);
      const layouts = {};
      MODULOS.forEach((m, i) => { layouts[m.value] = resLayouts[i]?.data?.layout || []; });
      setLayoutsPorModulo(layouts);
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    if (mounted) fetchDatos();
    return () => { mounted = false; };
  }, [fetchDatos]);

  const graficasVisibles = graficas.filter(
    (g) => !g.oculta && !modulosOcultos.includes(g.modulo),
  );
  const modulosConGraficas = MODULOS.filter(
    (m) => graficasVisibles.some((g) => g.modulo === m.value),
  );

  const graficasPorId = useMemo(
    () => new Map(graficasVisibles.map((g) => [String(g.id), g])),
    [graficasVisibles],
  );

  const onToggleModulo = async (modulo, visible) => {
    try {
      await API.put('configuraciones/modulos-ocultos/', { modulo, oculto: !visible });
      setModulosOcultos((prev) => (visible ? prev.filter((m) => m !== modulo) : [...prev, modulo]));
    } catch (err) {
      onError(err);
    }
  };

  const onToggleGrafica = async (graficaId, visible) => {
    try {
      await API.put(`configuraciones/configuraciones-graficas/${graficaId}/preferencia/`, { oculta: !visible });
      setGraficas((prev) => prev.map((g) => (g.id === graficaId ? { ...g, oculta: !visible } : g)));
    } catch (err) {
      onError(err);
    }
  };

  const onCambiarTipoPreferido = async (graficaId, tipoGraficaPreferido) => {
    try {
      await API.put(`configuraciones/configuraciones-graficas/${graficaId}/preferencia/`, {
        tipo_grafica_preferido: tipoGraficaPreferido || null,
      });
      setGraficas((prev) => prev.map((g) => (
        g.id === graficaId ? { ...g, tipo_grafica_preferido: tipoGraficaPreferido || null } : g
      )));
    } catch (err) {
      onError(err);
    }
  };

  const guardarLayout = async (modulo, layout) => {
    setLayoutsPorModulo((prev) => ({ ...prev, [modulo]: layout }));
    try {
      await API.put('configuraciones/layout-dashboard/', {
        modulo,
        layout: layout.map((item) => ({
          grafica: Number(item.i), x: item.x, y: item.y, w: item.w, h: item.h,
        })),
      });
    } catch (err) {
      onError(err);
    }
  };

  return (
    <Row className="container free-scroll-page">
      <Spin spinning={loading} style={{ width: '100%' }}>
        <Row justify="end" style={{ marginBottom: 16 }}>
          <SettingOutlined style={{ fontSize: 18, cursor: 'pointer' }} onClick={() => setShowPersonalizar(true)} />
        </Row>

        {modulosConGraficas.map((modulo) => {
          const graficasDelModulo = graficasVisibles.filter((g) => g.modulo === modulo.value);
          const layout = combinarLayoutGuardado(layoutsPorModulo[modulo.value], graficasDelModulo);

          return (
            <div key={modulo.value} style={{ marginBottom: 32, width: '100%' }}>
              <Title level={4}>{modulo.label}</Title>
              <GridLayoutConAncho
                className="layout"
                layout={layout}
                cols={COLUMNAS}
                rowHeight={ALTURA_FILA}
                margin={[16, 16]}
                draggableHandle=".grafica-drag-handle"
                onDragStop={(nuevoLayout) => guardarLayout(modulo.value, nuevoLayout)}
                onResizeStop={(nuevoLayout) => guardarLayout(modulo.value, nuevoLayout)}
              >
                {layout.map((item) => {
                  const grafica = graficasPorId.get(item.i);
                  if (!grafica) return null;
                  return (
                    <div key={item.i}>
                      <div
                        className="grafica-drag-handle"
                        style={{
                          cursor: 'move', textAlign: 'center', color: 'rgba(0,0,0,0.25)', lineHeight: '16px',
                        }}
                      >
                        <DragOutlined />
                      </div>
                      <div style={{ height: 'calc(100% - 16px)', overflow: 'auto' }}>
                        <GraficaCard grafica={grafica} esAdmin={false} />
                      </div>
                    </div>
                  );
                })}
              </GridLayoutConAncho>
            </div>
          );
        })}

        <Modal
          title="Personalizar gráficas"
          open={showPersonalizar}
          onCancel={() => setShowPersonalizar(false)}
          footer={null}
        >
          {MODULOS.map((modulo) => {
            const moduloVisible = !modulosOcultos.includes(modulo.value);
            const graficasDelModulo = graficas.filter((g) => g.modulo === modulo.value);
            if (!graficasDelModulo.length) return null;
            return (
              <div key={modulo.value} style={{ marginBottom: 16 }}>
                <Row justify="space-between" align="middle">
                  <Col><strong>{modulo.label}</strong></Col>
                  <Col>
                    <Switch
                      checked={moduloVisible}
                      onChange={(checked) => onToggleModulo(modulo.value, checked)}
                    />
                  </Col>
                </Row>
                {graficasDelModulo.map((grafica) => {
                  const esMultiMetrica = (grafica.metricas || []).length > 1;
                  return (
                    <Row
                      key={grafica.id}
                      justify="space-between"
                      align="middle"
                      gutter={8}
                      style={{ paddingLeft: 16, marginTop: 8 }}
                    >
                      <Col flex="auto">{grafica.nombre}</Col>
                      <Col>
                        <Select
                          size="small"
                          style={{ width: 150 }}
                          disabled={!moduloVisible || esMultiMetrica}
                          placeholder="Tipo original"
                          allowClear
                          value={grafica.tipo_grafica_preferido || undefined}
                          onChange={(tipo) => onCambiarTipoPreferido(grafica.id, tipo)}
                          onClear={() => onCambiarTipoPreferido(grafica.id, null)}
                        >
                          {TIPOS_GRAFICA.map((t) => (
                            <Option key={t.value} value={t.value}>{t.label}</Option>
                          ))}
                        </Select>
                      </Col>
                      <Col>
                        <Switch
                          size="small"
                          disabled={!moduloVisible}
                          checked={!grafica.oculta}
                          onChange={(checked) => onToggleGrafica(grafica.id, checked)}
                        />
                      </Col>
                    </Row>
                  );
                })}
              </div>
            );
          })}
        </Modal>
      </Spin>
    </Row>
  );
};

export default Dashboard;
